/**
 * The one place that converts between instants (UTC, what the DB stores) and the office's local
 * calendar (Asia/Kolkata by default). Everything about "which day does this punch belong to"
 * goes through `workDateFor` / `workDayBounds`. Client clocks are never used.
 */
import { DEFAULT_DAY_CUTOFF, TIMEZONE } from './constants.js';

export type Ymd = string; // 'YYYY-MM-DD'

export type LocalParts = {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number; // 0-23
  minute: number;
  second: number;
  /** ISO weekday: Mon=1 … Sun=7 */
  weekday: number;
};

const fmtCache = new Map<string, Intl.DateTimeFormat>();
function formatter(tz: string): Intl.DateTimeFormat {
  let f = fmtCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      weekday: 'short',
    });
    fmtCache.set(tz, f);
  }
  return f;
}

const WEEKDAYS: Record<string, number> = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };

export function localParts(instant: Date, tz: string = TIMEZONE): LocalParts {
  const parts: Record<string, string> = {};
  for (const p of formatter(tz).formatToParts(instant)) parts[p.type] = p.value;
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
    second: Number(parts.second),
    weekday: WEEKDAYS[parts.weekday ?? 'Mon'] ?? 1,
  };
}

export const pad2 = (n: number) => String(n).padStart(2, '0');

export function toYmd(p: { year: number; month: number; day: number }): Ymd {
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}`;
}

export function parseYmd(ymd: Ymd): { year: number; month: number; day: number } {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) throw new Error(`Bad date: ${ymd}`);
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
}

export function addDays(ymd: Ymd, n: number): Ymd {
  const { year, month, day } = parseYmd(ymd);
  return new Date(Date.UTC(year, month - 1, day + n)).toISOString().slice(0, 10);
}

/** ISO weekday of a calendar date: Mon=1 … Sun=7 */
export function isoWeekday(ymd: Ymd): number {
  const { year, month, day } = parseYmd(ymd);
  const js = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return js === 0 ? 7 : js;
}

export function mondayOf(ymd: Ymd): Ymd {
  return addDays(ymd, 1 - isoWeekday(ymd));
}

export function daysBetween(from: Ymd, to: Ymd): number {
  const a = parseYmd(from);
  const b = parseYmd(to);
  return Math.round(
    (Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)) / 86_400_000,
  );
}

export function parseHHMM(hhmm: string): number {
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(hhmm);
  if (!m) throw new Error(`Bad time: ${hhmm}`);
  return Number(m[1]) * 60 + Number(m[2]);
}

/** Offset of `tz` from UTC at `instant`, in minutes (IST = +330). */
export function tzOffsetMinutes(instant: Date, tz: string = TIMEZONE): number {
  const p = localParts(instant, tz);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return Math.round((asUtc - instant.getTime()) / 60_000);
}

/** Local wall-clock (date + minutes from midnight) in `tz` → instant. Handles DST zones by iterating. */
export function localToInstant(ymd: Ymd, minutesFromMidnight: number, tz: string = TIMEZONE): Date {
  const { year, month, day } = parseYmd(ymd);
  const naive = Date.UTC(year, month - 1, day) + minutesFromMidnight * 60_000;
  let guess = new Date(naive - tzOffsetMinutes(new Date(naive), tz) * 60_000);
  guess = new Date(naive - tzOffsetMinutes(guess, tz) * 60_000);
  return guess;
}

/** Calendar date in `tz` (no cutoff logic). */
export function localYmd(instant: Date, tz: string = TIMEZONE): Ymd {
  return toYmd(localParts(instant, tz));
}

/**
 * Which working day an instant belongs to.
 *
 * The day rolls over at the cutoff. Which calendar date names the day depends on where the
 * cutoff falls:
 *
 *   - A cutoff before noon (04:00, the original) is a morning start: the day is named for the
 *     date it starts on, so a punch-out at 00:30 still counts for the day it began.
 *   - A cutoff at or after noon (22:00, since 16 September 2026) is an evening close: the day
 *     is named for the date it ends on, so the whole of Wednesday's daytime is Wednesday, and
 *     22:30 on Wednesday is the first half-hour of Thursday.
 *
 * The first version applied the morning rule to both, which made 10 a.m. on the 17th "the 16th"
 * the moment the cutoff moved to ten at night. Found by a test that could only fail after 22:00.
 */
export function isEveningCutoff(cutoff: string): boolean {
  return parseHHMM(cutoff) >= 12 * 60;
}
export function workDateFor(
  instant: Date,
  tz: string = TIMEZONE,
  cutoff: string = DEFAULT_DAY_CUTOFF,
): Ymd {
  const p = localParts(instant, tz);
  const ymd = toYmd(p);
  const t = p.hour * 60 + p.minute;
  const c = parseHHMM(cutoff);
  if (isEveningCutoff(cutoff)) return t >= c ? addDays(ymd, 1) : ymd;
  return t < c ? addDays(ymd, -1) : ymd;
}

/** [start, end) instants of a working day. */
export function workDayBounds(
  workDate: Ymd,
  tz: string = TIMEZONE,
  cutoff: string = DEFAULT_DAY_CUTOFF,
): { start: Date; end: Date } {
  const c = parseHHMM(cutoff);
  // An evening day runs from yesterday's cutoff to today's; a morning day from today's to
  // tomorrow's. Same span either way — the label is what moves.
  if (isEveningCutoff(cutoff)) {
    return {
      start: localToInstant(addDays(workDate, -1), c, tz),
      end: localToInstant(workDate, c, tz),
    };
  }
  return {
    start: localToInstant(workDate, c, tz),
    end: localToInstant(addDays(workDate, 1), c, tz),
  };
}

/** Human "9:02 AM" in the office timezone. 12-hour since 16 September 2026; see fmt12. */
export function localHHMM(instant: Date, tz: string = TIMEZONE): string {
  const p = localParts(instant, tz);
  const h = p.hour % 12 === 0 ? 12 : p.hour % 12;
  return `${h}:${pad2(p.minute)} ${p.hour < 12 ? 'AM' : 'PM'}`;
}
